/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { UnifiedCollectionScreenType } from 'client-code/actions';
import { EpisodeInfo, GroupingType, SeasonsData } from 'client-code/episode-guide';

import { APPLICATION_FEATURE_AREA, COMMON_TIMEOUT_INACTIVITY_THRESHOLD } from '../../config';
import { DeviceModel } from '../../core/device';
import { NdvrInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import { ScreenController } from '../../core/navigation/ScreenController';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import type { RootState, TGetState, TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import {
    TEpisodeInfo,
    TEpisodeParams,
    TSeasonsParams,
    TUnifiedCollectionScreenDetails,
} from '../../core/types';
import { ListTypes, UniqueIdentifier } from '../../core/utils';
import { updateCommonInactivityTimeStampAction } from '../appl/appl.actions';
import { setListEntry, setListEntryFocus, setListEntryPosition } from '../list/list.actions';
import { onKeyDownList, onKeyUpList } from '../list/list.reducers';
import { IListState } from '../list/listprops';
import { CollectionFilters } from './CollectionFilters';
import { CollectionFilterType } from './CollectionFilterType';
import {
    resetCollectionScreenFilterAction,
    setCollectionFilterIsExactPageScrolled,
    setCollectionFilterSortOrder,
    setCollectionIsLastPage,
    setCollectionPageLoading,
    setCollectionScreenFilterDetailsAction,
    setCollectionScreenLastFocusedItemPositionAction,
} from './collectionscreen.actions';
import {
    RESET_ALL_COLLECTION_SCREEN_FILTERS,
    RESET_COLLECTION_SCREEN_FILTER,
    SET_COLLECTION_FILTER_IS_EXACT_PAGE_SCROLLED,
    SET_COLLECTION_FILTER_ITEMS_LOADING,
    SET_COLLECTION_FILTER_SORT_ORDER,
    SET_COLLECTION_IS_LAST_PAGE,
    SET_COLLECTION_PAGE_LOADING,
    SET_COLLECTION_SCREEN_CARET,
    SET_COLLECTION_SCREEN_FILTER_DETAILS,
    SET_COLLECTION_SCREEN_LAST_FOCUSED_ITEM_POSITION,
    SET_FOCUS_ON_COLLECTION_SCREEN_DELETE_BUTTON,
    SET_FOCUS_ON_COLLECTION_SCREEN_GO_TO_EPISODE_BUTTON,
} from './collectionscreen.types';
import { CollectionSortType } from './CollectionSortType';
import { PickerStripData } from './PickerStripData';
import { PickerStripType } from './PickerStripType';
import { TCollectionFilterDetails } from './TCollectionFilterDetails';
import { TListCollectionItem } from './TListCollectionItem';

const TAG = 'CollectionScreenReducer';

// Right Side items limit
export const COLLECTION_SCREEN_ITEMS_LIST_LIMIT = 11;
// As per name of constant says, it is help me to calculate pagination login and item focus.
// Need to make call when focus on second last tile of list and use press down button to
// show episode list for next page and set focus on first item of next page.
// Need to make call when focus on first tile of list and use press up button to
// show episode list for previous page and set focus on second last item of previous page.
export const CALIBRATION_VALUE_1 = 1;
export const CALIBRATION_VALUE_2 = 2;
export const CALIBRATION_VALUE_3 = 3;
const HOUR_IN_MILLISECONDS = 1 * 60 * 60 * 1000;

type SortOrderType = CollectionSortType | undefined;

type TCollectionStateType = {
    isLoadingFilterItems: boolean;
    isLoadingPage: boolean;
    isCaretVisible: boolean;
    isLastPage: boolean;
    filters: { [filter: string]: TCollectionFilterDetails };
};

type TFilterPayload = {
    isLoadingFilterItems: boolean;
    isLoadingPage: boolean;
    isCaretVisible: boolean;
    filter: string;
    filterDetails: TCollectionFilterDetails;
} & TCollectionFilterDetails;

const defaultInitialValues: TCollectionFilterDetails = {
    episodes: [],
    limit: 0,
    offset: 0,
    isExactPageScrolled: false,
    isDeleteButtonEnable: false,
    isGoToEpisodeButtonEnable: false,
    lastPosition: -1,
    totalItems: 0,
    sortOrder: undefined,
};

const initialState: TCollectionStateType = {
    isLoadingFilterItems: true,
    isLoadingPage: false,
    isCaretVisible: false,
    isLastPage: false,
    filters: {
        [CollectionFilters.NONE]: defaultInitialValues,
        [CollectionFilters.ALL_EPISODE_DETAIL]: {
            ...defaultInitialValues,
            sortOrder: CollectionSortType.BY_SEASON,
        },
        [CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL]: {
            ...defaultInitialValues,
            sortOrder: CollectionSortType.BY_SEASON,
        },
        [CollectionFilters.RECORDINGS_DETAILS]: {
            ...defaultInitialValues,
            sortOrder: CollectionSortType.BY_DATE,
        },
        [CollectionFilters.UPCOMING_DETAIL]: defaultInitialValues,
        [CollectionFilters.WATCH_LIST_DETAILS]: {
            ...defaultInitialValues,
            sortOrder: CollectionSortType.BY_SEASON,
        },
        [CollectionFilters.GET_THIS_SHOW_DETAILS]: defaultInitialValues,
        [CollectionFilters.ONEPASS_OPTIONS_DETAIL]: defaultInitialValues,
        [CollectionFilters.MORE_INFO_DETAIL]: defaultInitialValues,
        [CollectionFilters.CAST_DETAIL]: defaultInitialValues,
        [CollectionFilters.MAY_ALSO_LIKE_DETAIL]: defaultInitialValues,
    },
};

const collectionScreenReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<TFilterPayload>>{},
) => {
    switch (type) {
        case SET_COLLECTION_SCREEN_FILTER_DETAILS:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [payload.filter]: {
                        ...state.filters[payload.filter],
                        ...payload.filterDetails,
                    },
                },
            };
        case SET_FOCUS_ON_COLLECTION_SCREEN_DELETE_BUTTON:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [payload.filter]: {
                        ...state.filters[payload.filter],
                        isDeleteButtonEnable: payload.isDeleteButtonEnable,
                    },
                },
            };
        case SET_FOCUS_ON_COLLECTION_SCREEN_GO_TO_EPISODE_BUTTON:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [payload.filter]: {
                        ...state.filters[payload.filter],
                        isGoToEpisodeButtonEnable: payload.isGoToEpisodeButtonEnable,
                    },
                },
            };
        case SET_COLLECTION_SCREEN_LAST_FOCUSED_ITEM_POSITION:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [payload.filter]: {
                        ...state.filters[payload.filter],
                        lastPosition: payload.lastPosition,
                    },
                },
            };
        case SET_COLLECTION_FILTER_SORT_ORDER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [payload.filter]: {
                        ...state.filters[payload.filter],
                        sortOrder: payload.sortOrder,
                    },
                },
            };
        case SET_COLLECTION_FILTER_IS_EXACT_PAGE_SCROLLED:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [payload.filter]: {
                        ...state.filters[payload.filter],
                        isExactPageScrolled: payload.isExactPageScrolled,
                    },
                },
            };
        case SET_COLLECTION_FILTER_ITEMS_LOADING:
            return {
                ...state,
                isLoadingFilterItems: payload.isLoadingFilterItems,
            };
        case SET_COLLECTION_IS_LAST_PAGE:
            return {
                ...state,
                isLastPage: payload.isLastPage,
            };
        case SET_COLLECTION_PAGE_LOADING:
            return {
                ...state,
                isLoadingPage: payload.isLoadingPage,
            };
        case RESET_COLLECTION_SCREEN_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [payload.filter]: {
                        ...initialState.filters[payload.filter],
                        sortOrder: state.filters[payload.filter].sortOrder,
                    },
                },
            };
        case RESET_ALL_COLLECTION_SCREEN_FILTERS:
            return {
                ...state,
                filters: initialState.filters,
            };
        case SET_COLLECTION_SCREEN_CARET:
            return {
                ...state,
                isCaretVisible: payload.isCaretVisible,
            };
        default:
            return state;
    }
};

export const logError = (tag: string, error: any, shouldConsoleLog: boolean = true) => {
    const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
    Diagnostics.error(tag, errorMessage);
    if (shouldConsoleLog) Log.e(tag, errorMessage);
};

export const getPickerStripIndexBySeason = (stripData, seasonOrYear) => {
    if (seasonOrYear === -1) return stripData.length - 1;
    return stripData.findIndex((item) => {
        const [start, end] = item.split('-').map(Number);
        return seasonOrYear !== null
            ? seasonOrYear >= start && seasonOrYear <= (end || start)
            : true;
    });
};

export const getFocusedListByListState = (list: IListState): string => {
    if (list[ListTypes.COLLECTION_FILTERS]?.isFocused) return ListTypes.COLLECTION_FILTERS;
    if (list[ListTypes.COLLECTION_ITEMS]?.isFocused) return ListTypes.COLLECTION_ITEMS;
    if (list[ListTypes.COLLECTION_MORE_INFO]?.isFocused) return ListTypes.COLLECTION_MORE_INFO;
    if (list[ListTypes.COLLECTION_ONEPASS_ITEMS]?.isFocused)
        return ListTypes.COLLECTION_ONEPASS_ITEMS;
    if (list[ListTypes.COLLECTION_SEASON_PICKER]?.isFocused)
        return ListTypes.COLLECTION_SEASON_PICKER;
    return ListTypes.COLLECTION_FILTERS;
};

export const getAllFocusedListByListState = (list: IListState): string[] => {
    const focusedList: string[] = [];
    if (list[ListTypes.COLLECTION_FILTERS]?.isFocused)
        focusedList.push(ListTypes.COLLECTION_FILTERS);
    if (list[ListTypes.COLLECTION_ITEMS]?.isFocused) focusedList.push(ListTypes.COLLECTION_ITEMS);
    if (list[ListTypes.COLLECTION_MORE_INFO]?.isFocused)
        focusedList.push(ListTypes.COLLECTION_MORE_INFO);
    if (list[ListTypes.COLLECTION_ONEPASS_ITEMS]?.isFocused)
        focusedList.push(ListTypes.COLLECTION_ONEPASS_ITEMS);
    if (list[ListTypes.COLLECTION_SEASON_PICKER]?.isFocused)
        focusedList.push(ListTypes.COLLECTION_SEASON_PICKER);
    return focusedList;
};

export const getFiltersInfo = (): TUnifiedCollectionScreenDetails => {
    const sharedPreferences = DeviceModel.getSharedPreferences(
        SharedPrefConstants.COLLECTION_FILTERS_PREF_KEY,
    );
    return sharedPreferences.getJson(
        SharedPrefConstants.COLLECTION_FILTERS_INFO,
    ) as unknown as TUnifiedCollectionScreenDetails;
};

export const getSelectedFilterFromPosition = (position: number) => {
    const filtersInfo = getFiltersInfo();
    return (
        filtersInfo?.detailList?.details?.[position]?.presentation?.presentationId ||
        CollectionFilters.ALL_EPISODE_DETAIL
    );
};

export const getFilterType = (selectedFilter: string): CollectionFilterType => {
    if (selectedFilter === CollectionFilters.ALL_EPISODE_DETAIL) return CollectionFilterType.ALL;
    if (selectedFilter === CollectionFilters.RECORDINGS_DETAILS)
        return CollectionFilterType.RECORDINGS;
    if (selectedFilter === CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL)
        return CollectionFilterType.ON_DEMAND;
    return CollectionFilterType.WATCHLIST;
};

export const getSeasonsDataForSelectedFilter = async (
    selectedFilter: string,
    sortOrder: SortOrderType,
): Promise<SeasonsData> => {
    try {
        const {
            unifiedCollectionId = '',
            partnerId,
            collapsedVodItemId,
            contentSupplierPartnerId,
        } = getCollectionScreenInfo() || {};
        return new NdvrInteractor().getSeasonData({
            unifiedCollectionId,
            includeStickyInfo: true,
            filter: getFilterType(selectedFilter),
            sort: sortOrder,
            partnerId,
            collapsedVodItemId,
            contentSupplierPartnerId,
        } as TSeasonsParams);
    } catch (error: any) {
        logError(TAG, error);
        return {} as SeasonsData;
    }
};

export const isSeasonPickerDisplay = (seasonData: SeasonsData, sortOrder: SortOrderType) => {
    if (!seasonData.seasons) return false;
    const filtersInfo = getFiltersInfo();
    const isNotUcTeamScreen =
        filtersInfo?.unifiedCollectionScreenType !== UnifiedCollectionScreenType.TeamScreenDetails;
    if (
        isNotUcTeamScreen &&
        sortOrder === CollectionSortType.BY_SEASON &&
        seasonData.seasons?.length > CALIBRATION_VALUE_1
    ) {
        return true;
    }
    return false;
};

const isFirstPickerStrip = async (
    selectedFilter: string,
    sortOrder: SortOrderType,
    seasonOrYear: number,
): Promise<boolean> => {
    try {
        const seasonData = await getSeasonsDataForSelectedFilter(selectedFilter, sortOrder);
        const pickerStripMap = getPickerStripFromSeasons(seasonData);
        return pickerStripMap.keys().next().value === seasonOrYear || false;
    } catch (error: any) {
        logError(TAG, error);
        return false;
    }
};

const getCurrentPickerStrip = async (
    selectedFilter: string,
    sortOrder: SortOrderType,
    seasonOrYear: number,
): Promise<PickerStripData> => {
    try {
        const seasonData = await getSeasonsDataForSelectedFilter(selectedFilter, sortOrder);
        const pickerStripMap = getPickerStripFromSeasons(seasonData);
        return pickerStripMap.get(seasonOrYear) || ({} as PickerStripData);
    } catch (error: any) {
        logError(TAG, error);
        return {} as PickerStripData;
    }
};

const getPickerStripsOffset = async (
    selectedFilter: string,
    offset: number,
    limit: number,
    sortOrder: SortOrderType,
): Promise<number> => {
    try {
        const seasonData = await getSeasonsDataForSelectedFilter(selectedFilter, sortOrder);
        if (isSeasonPickerDisplay(seasonData, sortOrder)) {
            const pickerStripsWithLimit =
                getCurrentPagePickerStripFromSeasons(seasonData, offset, limit)?.length || 0;
            return (
                getCurrentPagePickerStripFromSeasons(
                    seasonData,
                    offset,
                    limit - pickerStripsWithLimit,
                )?.length || 0
            );
        }
        return 0;
    } catch (error: any) {
        logError(TAG, error);
        return 0;
    }
};

export const getCollectionScreenInfo = (): {
    unifiedCollectionId: string;
    transitionScreen: string;
    partnerId?: string;
    collapsedVodItemId?: string;
    contentSupplierPartnerId?: string;
} | null => {
    const collectionScreenInfo = ScreenController.getInstance()
        .getScreen<any>()
        .getProperties() as ReturnType<typeof getCollectionScreenInfo>;
    const listScreenSharedPreferenceEditor = DeviceModel.getSharedPreferences(
        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
    ).edit();
    listScreenSharedPreferenceEditor.putString(
        SharedPrefConstants.LIST_SCREEN_ID,
        collectionScreenInfo?.unifiedCollectionId || '',
    );
    listScreenSharedPreferenceEditor.commit();
    return collectionScreenInfo;
};

export const getEpisodeListForSelectedFilter = async (
    selectedFilter: string,
    offset: number,
    limit: number,
    sortOrder: SortOrderType,
): Promise<TEpisodeInfo> => {
    try {
        const {
            unifiedCollectionId = '',
            partnerId,
            collapsedVodItemId,
            contentSupplierPartnerId,
        } = getCollectionScreenInfo() || {};
        return new NdvrInteractor().getEpisodes({
            unifiedCollectionId,
            partnerId,
            collapsedVodItemId,
            contentSupplierPartnerId,
            limit,
            offset,
            filter: getFilterType(selectedFilter),
            sort: sortOrder,
            networkCallId: UniqueIdentifier.getNextCollectionFilterNetworkCallId(),
        } as TEpisodeParams);
    } catch (error: any) {
        logError(TAG, error);
        return {} as TEpisodeInfo;
    }
};

/**
 * @description Season Picker rules and supported business logic
 * ** Season Picker is used only when: **
 *  The episode list contains more than one season/yea
 *  The list is not sorted by date
 *  The list is not accessed from a Team Screen.
 *
 * ** Picker vs Header **
 * If the episode list contains 30 shows or fewer,
 * the Season Picker becomes a Season Header displaying only
 * the header section of the season picker and is not selectable or interactive.
 *
 * ** Other Rule **
 * Follow below doc for more information
 * http://w3-engr/MyWorld/UserInterface/Hydra/Specs/STB/ui_areas/content.html#season_picker
 */

// Seasons are grouped in groups of (N/6) rounded up to the nearest multiple of 5
const SEASON_PICKER_DIVISOR = 6;
// The years are grouped in groups of (N/3) rounded up to the nearest multiple of 5.
const YEAR_PICKER_DIVISOR = 3;
const MULTIPLICATION_THRESOLD = 5;
// SEASON PICKER: If a series or league has 10 or fewer seasons, on one line display
const SEASONS_THRESOLD = 10;
// YEAR PICKER: If series or league has episodes for 5 or fewer years,
const YEARS_THRESOLD = 5;
const SHORT_YEARS_CALIBRATION_VALUE = 100;

const getGroupSizeFromNoOfSeasons = (grouping: GroupingType, noOfSeasons: number): number => {
    const divisionfactor =
        grouping === GroupingType.Season ? SEASON_PICKER_DIVISOR : YEAR_PICKER_DIVISOR;
    let size = noOfSeasons / divisionfactor;
    size = Math.ceil(size);
    return Math.ceil(size / MULTIPLICATION_THRESOLD) * MULTIPLICATION_THRESOLD;
};

export const getStripData = (seasonData: SeasonsData, type: PickerStripType): string[] => {
    const stripData: string[] = [];
    if (!seasonData?.seasons?.length) return stripData;
    const { grouping = GroupingType.Season, seasons } = seasonData;
    const noOfSeasons = seasons.length;
    const groupSize = getGroupSizeFromNoOfSeasons(grouping, noOfSeasons);
    const isExtra = seasons[noOfSeasons - CALIBRATION_VALUE_1].seasonOrYear === -1;
    const isSeasonGroup =
        (grouping === GroupingType.Season && noOfSeasons > SEASONS_THRESOLD) || false;
    const isYearGroup = (grouping === GroupingType.Year && noOfSeasons > YEARS_THRESOLD) || false;
    const isGroup = isSeasonGroup || isYearGroup;
    const offset = isExtra ? CALIBRATION_VALUE_1 : 0;
    const remainingSeasons = (noOfSeasons - offset) % groupSize;
    if (type === PickerStripType.HEADER_STRIP) {
        seasons.forEach((season) => {
            if (season.seasonOrYear === -1) {
                stripData.push('Extra');
            } else {
                stripData.push(`Season ${season.seasonOrYear.toString()}`);
            }
        });
    } else if (isGroup) {
        const condition =
            noOfSeasons - (isExtra ? remainingSeasons + CALIBRATION_VALUE_1 : remainingSeasons);
        for (let j = 0; j < condition; ) {
            let endSeasonOrYear = seasons[j + groupSize - CALIBRATION_VALUE_1].seasonOrYear;
            endSeasonOrYear =
                grouping === GroupingType.Year
                    ? endSeasonOrYear % SHORT_YEARS_CALIBRATION_VALUE
                    : endSeasonOrYear;
            const group = `${seasons[j].seasonOrYear}-${endSeasonOrYear}`;
            stripData.push(group);
            j += groupSize;
        }
        if (remainingSeasons) {
            if (remainingSeasons === CALIBRATION_VALUE_1) {
                const lastSingleSeasonOffset = isExtra ? CALIBRATION_VALUE_2 : CALIBRATION_VALUE_1;
                const lastSingleSeason =
                    seasons[noOfSeasons - lastSingleSeasonOffset].seasonOrYear.toString();
                stripData.push(lastSingleSeason);
            } else {
                const startSeason = isExtra
                    ? remainingSeasons + CALIBRATION_VALUE_1
                    : remainingSeasons;
                const endSeason = isExtra ? CALIBRATION_VALUE_2 : 0;
                let endSeasonOrYear = seasons[noOfSeasons - endSeason].seasonOrYear;
                endSeasonOrYear =
                    grouping === GroupingType.Year
                        ? endSeasonOrYear % SHORT_YEARS_CALIBRATION_VALUE
                        : endSeasonOrYear;
                const remainingGroup = `${seasons[noOfSeasons - startSeason].seasonOrYear}-${endSeasonOrYear}`;
                stripData.push(remainingGroup);
            }
        }
        if (isExtra) {
            stripData.push('+');
        }
    } else {
        seasons.forEach((season) => {
            if (season.seasonOrYear === -1) {
                stripData.push('+');
            } else {
                stripData.push(season.seasonOrYear.toString());
            }
        });
    }
    return stripData;
};

export const getPickerStripDataFromSeasons = (
    seasonData: SeasonsData,
    type: PickerStripType,
): Map<number, PickerStripData> => {
    const seasonMap: Map<number, PickerStripData> = new Map();
    seasonData.seasons?.forEach((season, index, arr) => {
        let title = '';
        if (season.seasonOrYear === -1) {
            title = Locale.getString(LocaleKeys.EXTRA);
        } else {
            title =
                seasonData.grouping === GroupingType.Season
                    ? `${Locale.getString(LocaleKeys.SEASON)} ${season.seasonOrYear}`
                    : `${Locale.getString(LocaleKeys.YEAR)} ${season.seasonOrYear}`;
        }
        const subTitle = Locale.getString(LocaleKeys.JUMP_TO);
        if (seasonMap.size === 0) {
            seasonMap.set(season.seasonOrYear, {
                episodeCount: season.episodeCount,
                type,
                seasonOrYear: season.seasonOrYear,
                stripData: getStripData(seasonData, type),
                stripPosition: 0,
                title,
                subTitle,
                listPosition: 0,
            });
        } else {
            const prevEpisodeCount =
                seasonMap.get(arr[index - CALIBRATION_VALUE_1].seasonOrYear)?.episodeCount || 0;
            const prevPosition =
                seasonMap.get(arr[index - CALIBRATION_VALUE_1].seasonOrYear)?.stripPosition || 0;
            const stripPosition = prevEpisodeCount + prevPosition;
            seasonMap.set(season.seasonOrYear, {
                episodeCount: season.episodeCount,
                type,
                seasonOrYear: season.seasonOrYear,
                stripData: getStripData(seasonData, type),
                stripPosition,
                title,
                subTitle,
                listPosition: 0,
            });
        }
    });

    return seasonMap;
};

export const getPickerStripFromSeasons = (
    seasonData: SeasonsData,
): Map<number, PickerStripData> => {
    let stripType = PickerStripType.NONE;
    const totalEpisode = seasonData?.totalEpisodesCount || 0;
    if (totalEpisode <= 30) {
        stripType = PickerStripType.HEADER_STRIP;
    } else {
        stripType = PickerStripType.PICKER_STRIP;
    }
    return getPickerStripDataFromSeasons(seasonData, stripType);
};

// ========= End Picker Bussiness Logic ===========

export const getCurrentPagePickerStripFromSeasons = (
    seasonData: SeasonsData,
    offset: number,
    limit: number,
): PickerStripData[] => {
    const startIndex = offset;
    const endIndex = offset + limit;
    const strips = getPickerStripFromSeasons(seasonData);
    const currentStrips = [] as PickerStripData[];
    let stripCount = 0;
    strips.forEach((strip) => {
        if (strip.stripPosition >= startIndex && strip.stripPosition <= endIndex) {
            const updatedStrip = { ...strip };
            updatedStrip.listPosition = strip.stripPosition - offset + stripCount;
            currentStrips.push(updatedStrip);
            stripCount += CALIBRATION_VALUE_1;
        }
    });
    return currentStrips;
};

export const prepareAndGetEpisodeList = (
    seasonData: SeasonsData,
    episodeList: EpisodeInfo[],
    offset: number,
    limit: number,
): EpisodeInfo[] => {
    const modifiedEpisodeList = [...episodeList];
    const pickerStrips = getCurrentPagePickerStripFromSeasons(seasonData, offset, limit);
    pickerStrips.forEach((strip) => {
        modifiedEpisodeList.splice(strip.listPosition, 0, {
            contentId: '',
            title: strip.title,
            seasonOrYear: Number(strip.seasonOrYear),
        });
    });
    if (modifiedEpisodeList.length > limit) {
        const extra = modifiedEpisodeList.length - limit;
        modifiedEpisodeList.splice(modifiedEpisodeList.length - extra, extra);
    }
    return modifiedEpisodeList;
};

export const getSeasonPickerOverlayList = (
    seasonData: SeasonsData,
    startSeasonNumber: number,
    endSeasonNumber: number,
): string[] => {
    const pickerOverlayList = [startSeasonNumber.toString()];
    seasonData.seasons?.forEach((season) => {
        if (season.seasonOrYear > startSeasonNumber && season.seasonOrYear <= endSeasonNumber) {
            pickerOverlayList.push(season.seasonOrYear.toString());
        }
    });
    return pickerOverlayList;
};

export const isExactPageSize = async (
    totalItems: number,
    selectedFilter: string,
    sortOrder?: CollectionSortType,
) => {
    const seasonPickerStrips =
        selectedFilter !== CollectionFilters.UPCOMING_DETAIL
            ? (await getSeasonsDataForSelectedFilter(selectedFilter, sortOrder)).seasons?.length ||
              0
            : 0;
    // The total(episodes + seasons pickers) is exactly 10(1st page limit).
    return (
        totalItems! + 1 + seasonPickerStrips ===
        COLLECTION_SCREEN_ITEMS_LIST_LIMIT - CALIBRATION_VALUE_1
    );
};

export const fetchAndUpdateCollectionDetails =
    (highlightLastItem: boolean, newOffset: number, jumpPosition?: number) =>
    async (dispatch: Function, getState: Function) => {
        const { list, collectionScreen } = getState() as RootState;
        const { position = 0 } = list[ListTypes.COLLECTION_FILTERS];
        const selectedFilter = getSelectedFilterFromPosition(position);
        const { sortOrder, totalItems, offset } = collectionScreen.filters[selectedFilter];
        const collectionInfoSharedPreference = DeviceModel.getSharedPreferences(
            SharedPrefConstants.COLLECTION_INFO_PREF_KEY,
        );
        const { unifiedCollectionId = '' } = getCollectionScreenInfo() || {};

        const ndvrServices = new NdvrInteractor();
        let pageLimit = COLLECTION_SCREEN_ITEMS_LIST_LIMIT;
        pageLimit =
            newOffset === 0
                ? COLLECTION_SCREEN_ITEMS_LIST_LIMIT - CALIBRATION_VALUE_1
                : COLLECTION_SCREEN_ITEMS_LIST_LIMIT;
        let listLimit = 0;
        let episodesList: EpisodeInfo[] = [];
        const isExactPage = await isExactPageSize(totalItems!, selectedFilter, sortOrder);

        // Last page shoul have all accessible positions.
        let modifiedOffset = newOffset;
        const lastPageOffset = totalItems! - newOffset;
        if (collectionScreen.isLastPage) dispatch(setCollectionIsLastPage(false));
        let isLastPage = false;
        let pickerOffet = 0;
        if (isExactPage) modifiedOffset = 0;
        if (
            totalItems &&
            newOffset !== 0 &&
            lastPageOffset > 0 &&
            lastPageOffset <= COLLECTION_SCREEN_ITEMS_LIST_LIMIT - CALIBRATION_VALUE_3
        ) {
            pageLimit -= CALIBRATION_VALUE_1;
            pickerOffet = await getPickerStripsOffset(
                selectedFilter,
                newOffset,
                pageLimit,
                sortOrder,
            );
            modifiedOffset = totalItems - (pageLimit - CALIBRATION_VALUE_1) + pickerOffet;
            dispatch(setCollectionIsLastPage(true));
            isLastPage = true;
        }

        if (selectedFilter === CollectionFilters.UPCOMING_DETAIL) {
            const { minEndTime, requestToken } = collectionInfoSharedPreference.getJson(
                SharedPrefConstants.COLLECTION_UPCOMING_FILTER_INFO,
            ) as unknown as { minEndTime: string; requestToken: string };

            const minEndTimeInMs = new Date(minEndTime).getTime();
            const isMinEndTimeValid = Date.now() - minEndTimeInMs < HOUR_IN_MILLISECONDS;
            const modifiedMinEndTime = isMinEndTimeValid ? minEndTime : new Date().toISOString();

            if (!isMinEndTimeValid) {
                const collectionInfoSharedPrefEditor = collectionInfoSharedPreference.edit();
                collectionInfoSharedPrefEditor.putJson(
                    SharedPrefConstants.COLLECTION_UPCOMING_FILTER_INFO,
                    {
                        minEndTime: modifiedMinEndTime,
                        requestToken,
                    } as unknown as JSON,
                );
                collectionInfoSharedPrefEditor.commit();
            }

            const upcomingOffersRes = await ndvrServices.getUpcomingOffersForCollectionId({
                objectId: unifiedCollectionId,
                minEndTime: modifiedMinEndTime,
                offset: modifiedOffset,
                limit: pageLimit,
                requestToken,
                screenType: APPLICATION_FEATURE_AREA.SERIES_SCREEN,
            });
            listLimit = upcomingOffersRes.upcomingOffers.length;
            dispatch(
                setCollectionScreenFilterDetailsAction(selectedFilter, {
                    episodes: upcomingOffersRes.upcomingOffers,
                    offset: modifiedOffset,
                    limit: pageLimit,
                }),
            );
        } else {
            const seasonData = await getSeasonsDataForSelectedFilter(selectedFilter, sortOrder);
            const episodeRes = await getEpisodeListForSelectedFilter(
                selectedFilter,
                modifiedOffset,
                pageLimit,
                sortOrder,
            );
            episodesList = episodeRes.episodeInfo;
            listLimit = episodesList.length;
            if (isSeasonPickerDisplay(seasonData, sortOrder)) {
                episodesList = prepareAndGetEpisodeList(
                    seasonData,
                    episodesList,
                    modifiedOffset,
                    pageLimit,
                );
                listLimit = episodesList.length;
            }
            dispatch(
                setCollectionScreenFilterDetailsAction(selectedFilter, {
                    episodes: episodesList as TListCollectionItem[],
                    offset: modifiedOffset,
                    limit: pageLimit,
                }),
            );
        }

        dispatch(setCollectionPageLoading(false));

        const itemPosition = highlightLastItem
            ? CALIBRATION_VALUE_1
            : pageLimit - CALIBRATION_VALUE_2;
        let derivedPosition = jumpPosition !== undefined ? jumpPosition : itemPosition;
        if (!jumpPosition && newOffset === 0) {
            const pickerOffetFirstPage = await getPickerStripsOffset(
                selectedFilter,
                modifiedOffset,
                Math.abs(offset - modifiedOffset),
                sortOrder,
            );
            if (offset >= 0 && offset <= pageLimit - CALIBRATION_VALUE_2 - pickerOffetFirstPage) {
                derivedPosition = offset + pickerOffetFirstPage;
            }
        }
        if (!jumpPosition && isLastPage) {
            derivedPosition = pageLimit - lastPageOffset - pickerOffet;
        }
        dispatch(
            setListEntry(ListTypes.COLLECTION_ITEMS, {
                position: derivedPosition,
                limit: listLimit,
                isFocused: true,
            }),
        );
        if (
            episodesList.length &&
            derivedPosition >= 0 &&
            derivedPosition < episodesList.length &&
            !episodesList[derivedPosition].contentId &&
            selectedFilter !== CollectionFilters.UPCOMING_DETAIL
        ) {
            dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, false));
            dispatch(setListEntryFocus(ListTypes.COLLECTION_SEASON_PICKER, true));
        }
    };

export const onKeyDownCollectionScreenOperations =
    (jumpPosition?: number) => async (dispatch: Function, getState: Function) => {
        const { collectionScreen, list } = getState() as RootState;
        const { position: filterPosition } = list[ListTypes.COLLECTION_FILTERS];
        const selectedFilter = getSelectedFilterFromPosition(filterPosition);
        const {
            episodes: episodesList,
            offset,
            totalItems,
            sortOrder,
        } = collectionScreen.filters[selectedFilter];
        const { position: itemPosition, isFocused: isItemFocused } =
            list[ListTypes.COLLECTION_ITEMS];
        let pageLimit = COLLECTION_SCREEN_ITEMS_LIST_LIMIT;
        const position = jumpPosition ?? itemPosition;
        let isPickerHeader = false;

        if (collectionScreen.isLoadingPage) return;

        if (
            selectedFilter !== CollectionFilters.UPCOMING_DETAIL &&
            !isItemFocused &&
            !episodesList[position]?.contentId
        ) {
            dispatch(setListEntryFocus(ListTypes.COLLECTION_SEASON_PICKER, false));
            dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, true));
        }
        if (
            selectedFilter !== CollectionFilters.UPCOMING_DETAIL &&
            position < episodesList.length - CALIBRATION_VALUE_1 &&
            !episodesList[position + CALIBRATION_VALUE_1]?.contentId
        ) {
            const currentPickerStrip = await getCurrentPickerStrip(
                selectedFilter,
                sortOrder,
                (episodesList[position + CALIBRATION_VALUE_1] as EpisodeInfo).seasonOrYear || 0,
            );
            const stripType = currentPickerStrip.type;
            if (stripType === PickerStripType.PICKER_STRIP) {
                let pickerStripIndex = getPickerStripIndexBySeason(
                    currentPickerStrip.stripData,
                    currentPickerStrip.seasonOrYear,
                );
                dispatch(
                    setListEntryPosition(ListTypes.COLLECTION_SEASON_PICKER, pickerStripIndex),
                );
            }
            if (stripType === PickerStripType.HEADER_STRIP) {
                isPickerHeader = true;
                const tempPosition = position + CALIBRATION_VALUE_1;
                dispatch(
                    setListEntryPosition(
                        ListTypes.COLLECTION_ITEMS,
                        tempPosition + CALIBRATION_VALUE_1,
                    ),
                );
            } else {
                dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, false));
                dispatch(setListEntryFocus(ListTypes.COLLECTION_SEASON_PICKER, true));
            }
        }

        if (offset === 0) {
            pageLimit -= CALIBRATION_VALUE_1;
        }
        const pickerOffet = await getPickerStripsOffset(
            selectedFilter,
            offset,
            pageLimit,
            sortOrder,
        );
        const newOffset = offset + pageLimit - CALIBRATION_VALUE_2 - pickerOffet;
        const isExactPage = await isExactPageSize(totalItems || 0, selectedFilter, sortOrder);

        // Make API call when focus on second last tile of list and user press down button to
        // show episode list for next page. If last page don't do anything.
        // Set focus on first item of next page.
        if (
            position + CALIBRATION_VALUE_2 === pageLimit &&
            totalItems! > newOffset &&
            !collectionScreen.isLastPage &&
            (totalItems! + pickerOffet > pageLimit || isExactPage)
        ) {
            if (isExactPage) {
                dispatch(setCollectionFilterIsExactPageScrolled(selectedFilter, true));
                dispatch(onKeyDownList(ListTypes.COLLECTION_ITEMS));
                return;
            }
            dispatch(setCollectionPageLoading(true));
            dispatch(fetchAndUpdateCollectionDetails(true, newOffset));
        } else if (!isPickerHeader) dispatch(onKeyDownList(ListTypes.COLLECTION_ITEMS));
    };

export const onKeyUpCollectionScreenOperations =
    (jumpPosition?: number) => async (dispatch: Function, getState: Function) => {
        const { collectionScreen, list } = getState() as RootState;
        const { position: itemPosition, isFocused: isItemFocused } =
            list[ListTypes.COLLECTION_ITEMS];
        const position = jumpPosition ?? itemPosition;
        const { position: filterPosition } = list[ListTypes.COLLECTION_FILTERS];
        const selectedFilter = getSelectedFilterFromPosition(filterPosition);
        const {
            offset,
            episodes: episodesList,
            sortOrder,
            totalItems,
            isExactPageScrolled,
        } = collectionScreen.filters[selectedFilter];
        let pageLimit = COLLECTION_SCREEN_ITEMS_LIST_LIMIT;
        let isPickerHeader = false;

        if (collectionScreen.isLoadingPage) return;
        const isExactPage = await isExactPageSize(totalItems || 0, selectedFilter, sortOrder);

        const isFirstStrip = await isFirstPickerStrip(
            selectedFilter,
            sortOrder,
            (episodesList[position] as EpisodeInfo)?.seasonOrYear || 0,
        );
        if (
            !isFirstStrip &&
            selectedFilter !== CollectionFilters.UPCOMING_DETAIL &&
            !isItemFocused &&
            !episodesList[position]?.contentId
        ) {
            dispatch(setListEntryFocus(ListTypes.COLLECTION_SEASON_PICKER, false));
            dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, true));
        }
        if (
            selectedFilter !== CollectionFilters.UPCOMING_DETAIL &&
            position > 0 &&
            !episodesList[position - CALIBRATION_VALUE_1]?.contentId
        ) {
            const currentPickerStrip = await getCurrentPickerStrip(
                selectedFilter,
                sortOrder,
                (episodesList[position - CALIBRATION_VALUE_1] as EpisodeInfo).seasonOrYear || 0,
            );
            const stripType = currentPickerStrip.type;
            if (stripType === PickerStripType.PICKER_STRIP) {
                let pickerStripIndex = getPickerStripIndexBySeason(
                    currentPickerStrip.stripData,
                    currentPickerStrip.seasonOrYear,
                );
                dispatch(
                    setListEntryPosition(ListTypes.COLLECTION_SEASON_PICKER, pickerStripIndex),
                );
            }
            if (stripType === PickerStripType.HEADER_STRIP) {
                isPickerHeader = true;
                const tempPosition = position - CALIBRATION_VALUE_1;
                if (tempPosition === 0 && offset === 0) {
                    if (isExactPageScrolled)
                        dispatch(setCollectionFilterIsExactPageScrolled(selectedFilter, false));
                    return;
                }
                if (tempPosition !== 0)
                    dispatch(
                        setListEntryPosition(
                            ListTypes.COLLECTION_ITEMS,
                            tempPosition - CALIBRATION_VALUE_1,
                        ),
                    );
            } else {
                dispatch(setListEntryFocus(ListTypes.COLLECTION_ITEMS, false));
                dispatch(setListEntryFocus(ListTypes.COLLECTION_SEASON_PICKER, true));
            }
        }

        let newOffset = offset - pageLimit + CALIBRATION_VALUE_2;
        if (newOffset <= 0) {
            pageLimit -= CALIBRATION_VALUE_1;
        }
        const pickerOffet = await getPickerStripsOffset(
            selectedFilter,
            newOffset,
            pageLimit,
            sortOrder,
        );
        newOffset += pickerOffet;
        if (newOffset <= 0) {
            newOffset = 0;
        }

        // Make API call when focus on first tile of list and user press up button to
        // show episode list for previous page. If first page don't do anything.
        // set focus on second last item of previous page.
        if (
            position - CALIBRATION_VALUE_2 < 0 &&
            newOffset >= 0 &&
            (offset !== 0 || (offset === 0 && (collectionScreen.isLastPage || isExactPage)))
        ) {
            if (offset === 0 && isExactPageScrolled) {
                dispatch(setCollectionFilterIsExactPageScrolled(selectedFilter, false));
                return;
            }
            dispatch(setCollectionPageLoading(true));
            dispatch(fetchAndUpdateCollectionDetails(false, newOffset));
        } else if (!isPickerHeader) dispatch(onKeyUpList(ListTypes.COLLECTION_ITEMS));
    };

export const onKeyColorBlueCollectionScreen = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.COLLECTION_FILTERS]: { position },
        },
        collectionScreen,
    } = getState() as RootState;
    const selectedFilter = getSelectedFilterFromPosition(position);
    const { sortOrder } = collectionScreen.filters[selectedFilter];
    const unifiedCollectionId = getCollectionScreenInfo()?.unifiedCollectionId || '';

    if (
        selectedFilter === CollectionFilters.RECORDINGS_DETAILS ||
        selectedFilter === CollectionFilters.WATCH_LIST_DETAILS
    ) {
        dispatch(resetCollectionScreenFilterAction(selectedFilter));
        dispatch(updateBrowseStickiness());
        const collectionSharedPreference = DeviceModel.getSharedPreferences(
            SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_PREF_KEY,
        );
        const stickinessInfo = collectionSharedPreference.getJson(
            SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_INFO,
        ) as any;
        if (
            sortOrder &&
            stickinessInfo &&
            unifiedCollectionId &&
            stickinessInfo[unifiedCollectionId]
        ) {
            const { sortPositions } = stickinessInfo[unifiedCollectionId][selectedFilter];
            const updatedSortOrder =
                sortOrder === CollectionSortType.BY_DATE
                    ? CollectionSortType.BY_SEASON
                    : CollectionSortType.BY_DATE;
            const sortPosition = sortPositions[updatedSortOrder as string];
            if (sortPosition && sortPosition !== -1) {
                dispatch(
                    setCollectionScreenLastFocusedItemPositionAction(selectedFilter, sortPosition),
                );
            }
        }
        dispatch(
            setCollectionFilterSortOrder(
                selectedFilter,
                sortOrder === CollectionSortType.BY_DATE
                    ? CollectionSortType.BY_SEASON
                    : CollectionSortType.BY_DATE,
            ),
        );
    }
};

export const onCursorStateChangeCollectionScreenOperations =
    () => (dispatch: Function, getState: TGetState) => {
        const { list } = getState();
        const focusedList = getFocusedListByListState(list);

        if (!focusedList) dispatch(setListEntryFocus(ListTypes.COLLECTION_FILTERS, true));
    };

export const getBrowseStickinessKey = (unifiedCollectionId: string, partnerId: string): string => {
    return `${unifiedCollectionId}${partnerId}`;
};

export const updateBrowseStickiness = () => (_dispatch: Function, getState: Function) => {
    const { collectionScreen, list } = getState();
    const { isLoadingFilterItems } = collectionScreen;
    if (isLoadingFilterItems) return;
    const filtersInfo = getFiltersInfo();
    const { unifiedCollectionId = '', partnerId = '' } = getCollectionScreenInfo() || {};
    const key = getBrowseStickinessKey(unifiedCollectionId, partnerId);
    if (!key) return;
    if (filtersInfo?.detailList?.details?.length) {
        const collectionSharedPreference = new SharedPreferences(
            SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_PREF_KEY,
        );
        let collections = collectionSharedPreference.getJson(
            SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_INFO,
        );
        if (!collections) {
            collections = {} as JSON;
        }
        if (!collections[key]) {
            collections[key] = {};
        }
        filtersInfo.detailList.details.forEach((filter) => {
            if (
                filter?.presentation?.presentationId &&
                filter?.presentation?.presentationId !== CollectionFilters.GET_THIS_SHOW_DETAILS &&
                filter?.presentation?.presentationId !== CollectionFilters.ONEPASS_OPTIONS_DETAIL &&
                filter?.presentation?.presentationId !== CollectionFilters.MORE_INFO_DETAIL
            ) {
                const { position: filterPosition } = list[ListTypes.COLLECTION_FILTERS];
                const selectedFilter = getSelectedFilterFromPosition(filterPosition);
                const { offset, lastPosition, sortOrder, isExactPageScrolled } =
                    collectionScreen.filters[filter?.presentation?.presentationId];
                let tSortPositions = {
                    [CollectionSortType.BY_DATE]: -1,
                    [CollectionSortType.BY_SEASON]: -1,
                };
                const tFilterParams = collections[key][filter?.presentation?.presentationId];
                if (tFilterParams && tFilterParams.sortPositions) {
                    tSortPositions = tFilterParams.sortPositions;
                }
                const isSortPositionsApplied =
                    sortOrder &&
                    filter?.presentation?.presentationId !== CollectionFilters.ALL_EPISODE_DETAIL &&
                    filter?.presentation?.presentationId !== CollectionFilters.UPCOMING_DETAIL &&
                    filter.presentation?.presentationId !==
                        CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL;
                if (filter?.presentation?.presentationId === selectedFilter) {
                    const { position } = list[ListTypes.COLLECTION_ITEMS];
                    collections[key][filter?.presentation?.presentationId] = {
                        lastPosition: position,
                        offset,
                        isExactPageScrolled,
                        sortOrder,
                        ...(isSortPositionsApplied
                            ? {
                                  sortPositions: {
                                      ...tSortPositions,
                                      [sortOrder as string]: position,
                                  },
                              }
                            : {}),
                    };
                } else {
                    collections[key][filter?.presentation?.presentationId] = {
                        lastPosition,
                        offset,
                        isExactPageScrolled,
                        sortOrder,
                        ...(isSortPositionsApplied
                            ? {
                                  sortPositions: {
                                      ...tSortPositions,
                                      [sortOrder as string]: lastPosition,
                                  },
                              }
                            : {}),
                    };
                }
            }
        });
        const collectionSharedPreferenceEditor = collectionSharedPreference.edit();
        collectionSharedPreferenceEditor.putJson(
            SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_INFO,
            collections as unknown as JSON,
        );
        collectionSharedPreferenceEditor.commit();
    }
};

export const clearBrowseStickinessInCollectionScreen =
    () => (dispatch: Function, getState: Function) => {
        const { commonInactivityTimeStamp } = getState().appl;
        const totalInactiveDuration = Date.now() - commonInactivityTimeStamp;
        if (totalInactiveDuration >= COMMON_TIMEOUT_INACTIVITY_THRESHOLD) {
            const collectionSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.COLLECTION_BROWSE_STICKINESS_PREF_KEY,
            ).edit();
            collectionSharedPreferenceEditor.clear();
            collectionSharedPreferenceEditor.commit();
        }
        dispatch(updateCommonInactivityTimeStampAction());
    };

export default collectionScreenReducer;
